/**
 * Theme utility — manages dark/light/auto theme via CSS custom properties.
 *
 * Sets data-theme attribute on <html> to switch between theme variable blocks
 * defined in index.css. Does NOT use React state — this is a DOM-level utility.
 *
 * Theme is persisted to localStorage for instant application on page load
 * (before React hydrates), eliminating the flash of wrong theme (FOUC).
 */

type ThemeMode = 'dark' | 'light' | 'auto';

const THEME_STORAGE_KEY = 'paraclete-theme';

// Initialize from localStorage to avoid FOUC
let currentMode: ThemeMode = (() => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light' || stored === 'auto') return stored;
  } catch {}
  return 'dark';
})();

let mediaQueryListener: ((e: MediaQueryListEvent) => void) | null = null;

/**
 * Set the active theme.
 * @param mode - 'dark', 'light', or 'auto' (follows system preference)
 */
export function setTheme(mode: ThemeMode | string | null | undefined): void {
  const resolvedMode = (mode === 'dark' || mode === 'light' || mode === 'auto')
    ? mode
    : 'dark'; // Default fallback

  currentMode = resolvedMode;

  // Persist to localStorage for instant load on refresh
  try { localStorage.setItem(THEME_STORAGE_KEY, resolvedMode); } catch {}

  // Remove any existing auto-mode listener
  if (mediaQueryListener) {
    window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', mediaQueryListener);
    mediaQueryListener = null;
  }

  if (resolvedMode === 'auto') {
    // Apply based on system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');

    // Listen for system theme changes
    mediaQueryListener = (e: MediaQueryListEvent) => {
      document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light');
    };
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', mediaQueryListener);
  } else {
    document.documentElement.setAttribute('data-theme', resolvedMode);
  }
}

/**
 * Get the current theme mode.
 */
export function getTheme(): ThemeMode {
  return currentMode;
}

// ============================================================
// Font Switching
// ============================================================

const FONT_FAMILIES: Record<string, string> = {
  'inter': "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  'system': "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif",
  'dyslexic': "'OpenDyslexic', -apple-system, BlinkMacSystemFont, sans-serif",
};

/**
 * Set the chat font.
 * @param font - 'inter', 'system', or 'dyslexic'
 */
export function setFont(font: string | null | undefined): void {
  const family = FONT_FAMILIES[font || 'system'] || FONT_FAMILIES['system'];
  document.documentElement.style.setProperty('--font-chat', family);
}
